import styled, { css } from 'styled-components';
import { Containers } from '../YourSolution/YS/Components/Shared/Step.styles';
import brand from '@helpers/brand';
import Heading from '@stories/Components/Global/Typography/Heading';
import { fonts } from '@helpers/fonts';
import Paragraph from '@stories/Components/Global/Typography/Paragraph';
import { LoginModalStyles } from '../Forms/LoginModal/LoginModal.styles';
import { ButtonStyles } from '@stories/Components/Buttons/Button/Button.styles';
import { fluid } from '@helpers/fluid';
import { Device, from, until } from '@helpers/media';
import { transparentize } from 'polished';
import { ThemeSwitch } from '@helpers/theme';
const Container = styled(Containers.FullWidth) `
  background-color: ${brand.grey.grey96};
  ${ThemeSwitch({ groundforceColour: brand.primary.alt })};
  border-top: 3px solid var(--theme);
  padding: ${fluid(24, 33)} 0px;
  --padding: ${fluid(12, 142)};

  @media ${until(Device.TabletLarge)} {
    margin-bottom: 15px;
  }
`;
const TopSection = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${fluid(8, 16)};
  padding-left: var(--padding);
`;
const Title = styled(Heading) `
  ${fonts.DaxPro.Medium};
  text-transform: unset;
  font-size: ${fluid(24, 40)};
  line-height: ${fluid(30, 35)};
  color: #575757;
`;
const Subtitle = styled(Paragraph) `
  ${fonts.DaxPro.Regular};
  margin-bottom: 0;
`;
const Split = styled.div `
  margin-top: ${fluid(22, 38)};

  @media ${until(Device.TabletLarge)} {
    display: flex;
    flex-direction: column;
  }

  @media ${from(Device.TabletLarge)} {
    grid-template-columns: repeat(12, 1fr);
    display: grid;
  }
`;
const LeftSection = styled.div `
  grid-column: span 5;
  padding: ${fluid(13, 31)} 0px ${fluid(13, 31)} var(--padding);
  ${ThemeSwitch({ groundforceColour: brand.primary.alt })};
  background-color: var(--theme);

  @media ${until(Device.TabletLarge)} {
    padding-right: var(--padding);
  }
`;
const GreenShape = styled.div `
  height: 100%;
  ${ThemeSwitch({ groundforceColour: brand.primary.alt })};
  background-color: var(--theme);
  clip-path: polygon(0 0, 100% 0, 50% 100%, 0% 100%);
  position: relative;
  left: -1px;

  @media ${from(Device.TabletLarge)} {
    grid-column: span 1;
  }
  @media ${from(Device.Desktop)} {
    grid-column: span 2;
  }

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const SelectedProductsText = styled(Paragraph) `
  color: ${brand.white};
  margin-bottom: 10px;
`;
const ProductsList = styled.div `
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media ${from(Device.TabletLarge)} {
    max-height: 696px;
    overflow-y: auto;
    direction: rtl;
    ${ThemeSwitch({ groundforceColour: '#517900', tpaColour: '#18225C' })};

    &[data-has-overflow='true'] {
      left: -40px;
      padding-left: 28px;
      width: calc(100% + 40px);
    }

    &::-webkit-scrollbar {
      width: 14px;
      position: relative;
      left: 8px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #bcbcbb;
      border-radius: 14px;
    }

    &::-webkit-scrollbar-track {
      background: var(--theme);
      border-radius: 14px;
    }
  }

  position: relative;
`;
const ProductCard = styled.div `
  display: flex;
  align-items: center;
  padding: 8px;
  background-color: ${brand.white};

  @media ${from(Device.TabletLarge)} {
    direction: ltr;
  }

  img {
    width: ${fluid(56, 111)};
    height: ${fluid(56, 111)};
    aspect-ratio: 1 / 1;
    object-fit: cover;
    object-position: center center;
  }

  span {
    padding-left: ${fluid(12, 23)};
    ${fonts.DaxPro.Medium}
    flex-grow: 1;
    font-size: ${fluid(16, 20)};
    line-height: ${fluid(20, 25)};
    ${ThemeSwitch({ groundforceColour: brand.primary.alt })};
    color: var(--theme);
    padding-right: fluid(8, 12);
  }
`;
const RemoveButton = styled.div `
  width: 32px;
  height: 32px;
  background-color: ${brand.accent.orange};
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
  transition: opacity 0.25s ease-in-out;

  &[data-disabled='true'] {
    opacity: 0.5;
  }

  svg {
    width: 12px;
    height: 12px;
    color: ${brand.white};
  }
`;
const FormWrapper = styled.div `
  padding: ${fluid(22, 50)} var(--padding) 0px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media ${until(Device.TabletLarge)} {
    padding-left: var(--padding);
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: span 6;
    ${ButtonStyles.Button} {
      width: max-content;
      max-width: 100%;
      min-width: 144px;
      margin-left: auto;
    }
  }

  @media ${from(Device.Desktop)} {
    grid-column: span 5;
  }
`;
const FormRow = styled.div `
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr;

  @media ${from(Device.TabletLarge)} {
    grid-template-columns: 1fr 1fr;
    ${({ wide }) => wide &&
    css `
        grid-template-columns: 1fr;
      `}
  }

  ${LoginModalStyles.Label} {
    margin-bottom: 8px;
  }

  ${LoginModalStyles.Field} {
    margin-bottom: 0;
  }

  ${LoginModalStyles.TextArea} {
    min-height: 150px;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  ${LoginModalStyles.Input}, ${LoginModalStyles.TextArea} {
    padding-left: 12px;
    padding-right: 12px;
    border-radius: 4px;

    &::placeholder {
      color: #575757;
      opacity: 0.5;
    }
  }
`;
const BasketMessage = styled.div `
  padding: 15px;
  margin-top: 15px;
  ${ThemeSwitch({
    groundforceColour: brand.primary.alt,
    groundforceColourHover: transparentize(0.9, brand.primary.alt),
    tpaColourHover: transparentize(0.9, brand.tpa.base),
})};
  border: 2px solid var(--theme);
  background-color: var(--theme-hover);
  display: flex;
  flex-direction: column;
  grid-column: 1 / -1;
`;
const BasketMessageTitle = styled(Paragraph) ``;
const BasketMessageInner = styled.p `
  a {
    ${ThemeSwitch({ groundforceColour: brand.primary.alt })};
    color: var(--theme);
    text-decoration: underline;
    cursor: pointer;
  }
`;
const SuccessMessage = styled(Paragraph) `
  grid-column: span 12;
  ${ThemeSwitch({ groundforceColour: brand.primary.alt })};
  color: var(--theme);
  margin: 0;
  padding-left: var(--padding);
`;
export const EnquiryBasketStyles = {
    Container,
    TopSection,
    Title,
    Subtitle,
    Split,
    LeftSection,
    SelectedProductsText,
    GreenShape,
    ProductsList,
    ProductCard,
    RemoveButton,
    FormWrapper,
    FormRow,
    BasketMessage,
    BasketMessageTitle,
    BasketMessageInner,
    SuccessMessage,
};
